import React, { useState } from 'react'
import { Project } from '../../types/projects'
import { IconCompass, IconPhoto, IconDeviceDesktop, IconArrowLeft, IconDatabase } from '@tabler/icons-react'
import { Layer } from '@feltmaps/js-sdk'

import { cn, formatArea } from '../../enviroment/lib/utils'
import TooltipWrapper from '../atoms/TooltipWrapper'
import Typography from '../atoms/Typography'
import { GradientBorderButton } from '../atoms/buttons/GradientBorderButton'
import { DataRoomDialog } from '../organisms/data-room'
import LayerList from '../organisms/LayersList'
import { LayerUploadStatus } from '../../types/layer'

const CALENDAR_URL = 'https://calendar.app.google/uGSJrbgw6ntvNveQ7'

export const CREATE_PROJECT_STAGE_NAMES = ['Exploring', 'Implementation', 'Monitoring'] as const

const STAGE_ICONS = {
  Exploring: IconCompass,
  Implementation: IconPhoto,
  Monitoring: IconDeviceDesktop,
} as const

interface ProjectDetailsHeaderProps {
  project: Project
  onBack: () => void
  layers?: Layer[]
  onExport?: (layer: Layer) => void
  copaStatus?: LayerUploadStatus
  copaSummary?: string | null
  stage?: { id: string; name: string } | null
}

const ProjectDetailItem = ({ header, text }: { header: string; text: string }) => {
  return (
    <div className="min-w-0 max-w-xs rounded-lg border border-gray-300 bg-transparent px-4 py-2.5">
      <TooltipWrapper content={header} side="bottom" delayDuration={500}>
        {/* A plain element: Typography cannot take the ref Radix anchors the tooltip to */}
        <div className="truncate font-inter text-xs uppercase tracking-wide text-gray-300">{header}</div>
      </TooltipWrapper>
      <TooltipWrapper content={text} side="bottom" delayDuration={500}>
        <div className="mt-1 truncate text-xs font-normal text-gray-50">{text}</div>
      </TooltipWrapper>
    </div>
  )
}

const SkeletonDetailItem = ({ header }: { header: string }) => (
  <div className="min-w-0 max-w-xs rounded-lg border border-gray-300 bg-transparent px-4 py-2.5">
    <Typography variant="xs" className="uppercase tracking-wide text-gray-300">
      {header}
    </Typography>
    <div className="mt-2 h-3 w-20 animate-pulse rounded bg-gray-700" />
  </div>
)

const ProjectDetailsHeader: React.FC<ProjectDetailsHeaderProps> = ({
  project,
  onBack,
  layers = [],
  onExport,
  copaStatus,
  copaSummary,
  stage: statusStage,
}) => {
  const [isDataRoomOpen, setIsDataRoomOpen] = useState(false)

  return (
    <div className="bg-gray-900 px-7 py-5 text-white">
      <div>
        <div className="flex items-start justify-between gap-6">
          <div className="flex max-w-[50%] flex-shrink-0 flex-col gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-xs text-gray-50 transition-colors hover:text-gray-100"
            >
              <IconArrowLeft className="h-4 w-4" />
              Back to projects
            </button>
            <h1 className="text-lg font-normal text-gray-50 [overflow-wrap:anywhere]">{project.name}</h1>
          </div>

          <div className="flex min-w-0 items-center gap-3">
            {copaStatus === LayerUploadStatus.PROCESSING ? (
              <SkeletonDetailItem header="project type" />
            ) : (
              <ProjectDetailItem header="project type" text={project.types.map((t) => t.name).join(', ') || '-'} />
            )}
            {copaStatus === LayerUploadStatus.PROCESSING ? (
              <SkeletonDetailItem header="project phase" />
            ) : (
              <ProjectDetailItem header="project phase" text={(statusStage ?? project.stage)?.name || '-'} />
            )}
            <ProjectDetailItem header="project size" text={formatArea(project.totalAreaHectares)} />
            {project.ratingFramework && project.ratingGrade && (
              <ProjectDetailItem header={project.ratingFramework.name} text={project.ratingGrade} />
            )}
          </div>
        </div>
      </div>

      <div className="pt-4">
        <div className="grid grid-cols-3 gap-4">
          {CREATE_PROJECT_STAGE_NAMES.map((stageName) => {
            const activeStage = statusStage ?? project.stage
            const isCurrent = activeStage?.name === stageName
            const Icon = STAGE_ICONS[stageName]

            return (
              <div
                key={stageName}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-6 py-3 transition-all',
                  isCurrent ? 'border border-orange-500 bg-gray-800' : 'border border-gray-800 bg-gray-800',
                )}
              >
                <div
                  className={cn('flex items-center justify-center', isCurrent ? 'text-orange-500' : 'text-gray-200')}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn('text-sm font-medium', isCurrent ? 'text-gray-50' : 'text-gray-200')}>
                    {stageName.toUpperCase()}
                  </span>
                  {isCurrent && <span className="text-sm font-normal text-orange-500">Current</span>}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="relative mt-4 flex items-center justify-between">
        <div className="min-w-0 flex-1">
          {copaStatus === LayerUploadStatus.PROCESSING ? (
            <div className="flex flex-col gap-2">
              <p className="text-xs uppercase tracking-wide text-gray-300">Project Description</p>
              <div className="h-3 w-3/4 animate-pulse rounded bg-gray-700" />
            </div>
          ) : copaStatus === LayerUploadStatus.COMPLETED && copaSummary ? (
            <>
              <Typography variant="sm" className="uppercase tracking-wide text-gray-300">
                Project Description
              </Typography>
              <div className="mt-1">
                <Typography variant="sm">{copaSummary}</Typography>
              </div>
            </>
          ) : (
            <>
              <Typography variant="sm" className="uppercase tracking-wide text-gray-300">
                Steps for next phase
              </Typography>
              <div className="mt-1">
                <Typography variant="sm">
                  Need support with the next phase of your project?{' '}
                  <a href={CALENDAR_URL} target="_blank" rel="noopener noreferrer" className="underline">
                    Talk to our team &#x2192;
                  </a>
                </Typography>
              </div>
            </>
          )}
        </div>

        <div className="ml-6 flex flex-shrink-0 items-center gap-3">
          <GradientBorderButton gradientSize="sm" textSize="sm" onClick={() => setIsDataRoomOpen(true)}>
            <IconDatabase className="h-4 w-4" />
            Visit Data Room
          </GradientBorderButton>

          {/* Export pulls layers from Felt — an embedded project has none */}
          {!project.embedUrl && <LayerList layers={layers} onExport={(layer) => onExport?.(layer)} />}
        </div>
      </div>

      <DataRoomDialog isOpen={isDataRoomOpen} onClose={() => setIsDataRoomOpen(false)} project={project} />
    </div>
  )
}

export default ProjectDetailsHeader
